import React, { useMemo, useState } from 'react';

import { CoreLink, RecursiveMenu } from '@uncinc/react-drupal-core';
import { Button, Container } from '@uncinc/uncinc-react-kitchen-sink';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import './style.scss';
import type { MenuItemLinkElementProps } from '@uncinc/react-drupal-core/dist/Components/RecursiveMenu/types';

const MenuItemLinkElement: React.FC<MenuItemLinkElementProps> = ({
  item,
  url,
  title,
  description,
  alterUrlFunction,
  onClickFunction,
  minLevel,
  maxLevel,
  linkIconName,
  linkIconFlair = 'outlined',
  buildMenu,
  MenuItemWrapper,
  MenuItemTitle,
  linkProps,
}) => {
  const { t } = useTranslation();
  const [showChildren, setShow] = useState(false);
  const hasChildren = item.has_children && !(item.depth >= maxLevel);

  const classes = useMemo(() => classNames('RecursiveMenu__childwrapper', showChildren ? 'show' : 'hide'), [showChildren]);


  const handleClick = () => {
    setShow(!showChildren);
  };

  return (
    <>
      {hasChildren
        ? (
          <button type="button" className="RecursiveMenu__titlewrapper" onClick={handleClick}>
            <MenuItemTitle
              url={undefined}
              title={title}
              alterUrlFunction={alterUrlFunction}
              onClickFunction={undefined}
              linkIconName={linkIconName}
              linkIconFlair={linkIconFlair}
              linkProps={linkProps}
              level={item.depth}
            />
            <span aria-hidden="true" className={classNames('RecursiveMenu__expand-icon', { 'RecursiveMenu__expand-icon--open': showChildren })} />
          </button>
        )
        : (
          <div className="RecursiveMenu__titlewrapper">
            <MenuItemTitle
              url={url}
              title={title}
              alterUrlFunction={alterUrlFunction}
              onClickFunction={onClickFunction}
              linkIconName={linkIconName}
              linkIconFlair={linkIconFlair}
              linkProps={linkProps}
              level={item.depth}
            />
          </div>
        )}

      {description && (
        <div className="RecursiveMenu__description">{description}</div>
      )}

      {/* If we haven't reached the maxLevel, render the next layer, recursively. */}
      {(hasChildren && (
        <div className={classes}>
          <MenuItemWrapper
            level={item.depth - (minLevel ? minLevel - 1 : 0)}
            buildMenu={buildMenu}
          >
            {item.subtree}
          </MenuItemWrapper>
          {url && (
            <CoreLink
              href={alterUrlFunction ? alterUrlFunction(url) : url}
              className="RecursiveMenu__parent-link"
              onClick={onClickFunction}
            >
              {t('general.to_section')} {title.toLowerCase()}
            </CoreLink>
          )}
        </div>
      ))}
    </>
  );
};

const ContactMenuItemLinkElement: React.FC<MenuItemLinkElementProps> = ({ url, title }) => (
  <Button href={url} inverted>
    {title}
  </Button>
);

const Overlay = ({ toggleOverlayOpen }: { toggleOverlayOpen: () => void }) => {
  const { t } = useTranslation();
  const menuIdMain = 'main';

  return (
    <div className="Overlay" id="main-nav-overlay">
      <div className="Overlay__background">
        <Container>
          <div className="Overlay__wrapper">
            <div className="Overlay__wrapper__section mainsecondary">
              <RecursiveMenu
                menuId={menuIdMain}
                maxLevel={2}
                MenuItemLinkElement={MenuItemLinkElement}
                onClickFunction={() => toggleOverlayOpen()}
                navProps={{ 'aria-label': t(`menu_label.${menuIdMain}`) }}
              />
              <RecursiveMenu
                menuId="secondary"
                maxLevel={1}
                className="Overlay__contact"
                MenuItemLinkElement={ContactMenuItemLinkElement}
                onClickFunction={() => toggleOverlayOpen()}
                navProps={{ 'aria-label': t('menu_label.secondary') }}
              />
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
};

export default Overlay;
